import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal } from 'antd';
import { CloseOutlined, CrownOutlined, StarOutlined, ThunderboltOutlined } from '@ant-design/icons';
import useTranslation from '@hooks/useTranslation';
import { selectPremiumUpgradeModalVisible, showPremiumUpgradeModal } from '@src/features/interface/interfaceSlice';
import { openCheckoutModalAction } from '@src/app/reducers/checkout/checkoutSlice';
import styles from './PremiumUpgradeModal.module.css';

/**
 * Premium upgrade modal that displays a custom design matching the provided mockup
 * Controls its own visibility through Redux state
 * @returns React.JSX.Element
 */
const PremiumUpgradeModal = (): React.JSX.Element => {
  const t = useTranslation('PremiumUpgradeModal');
  const dispatch = useDispatch();
  const visible = useSelector(selectPremiumUpgradeModalVisible);

  const handleCancel = useCallback(() => {
    dispatch(showPremiumUpgradeModal(false));
  }, [dispatch]);

  const handleUpgrade = useCallback(() => {
    dispatch(showPremiumUpgradeModal(false));
    dispatch(openCheckoutModalAction());
  }, [dispatch]);

  const handleMaybeLater = useCallback(() => {
    dispatch(showPremiumUpgradeModal(false));
  }, [dispatch]);

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      centered
      width={400}
      className={styles.Modal}
      classNames={{
        content: styles.ModalContent,
      }}
      maskClosable={true}
      closable={false}
      destroyOnClose={true}
    >
      <div className={styles.Container}>
        {/* Header Section with Gradient */}
        <div className={styles.Header}>
          <button className={styles.CloseButton} onClick={handleCancel}>
            <CloseOutlined />
          </button>
          
          <div className={styles.CrownIcon}>
            <CrownOutlined />
          </div>
          
          <h2 className={styles.Title}>{t('Title')}</h2>
          <p className={styles.Description}>{t('Description')}</p>
        </div>

        {/* Content Section */}
        <div className={styles.Content}>
          <div className={styles.FeaturesList}>
            {Array.from({ length: 6 }, (_, index) => (
              <div key={index} className={styles.Feature}>
                <ThunderboltOutlined className={styles.FeatureIcon} />
                <span>{t(`Feature${index + 1}`)}</span>
              </div>
            ))}
          </div>

          <button className={styles.UpgradeButton} onClick={handleUpgrade}>
            <StarOutlined className={styles.ButtonIcon} />
            {t('UpgradeNow')}
          </button>

          <button className={styles.MaybeLaterButton} onClick={handleMaybeLater}>
            {t('MaybeLater')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PremiumUpgradeModal;
